import { useTranslation } from "next-i18next";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { FC, Fragment, useEffect } from "react";
import { useCookies } from "react-cookie";
import Card from "~/components/Card";
import { Container } from "~/components/Container";
import Divider from "~/components/Divider";
import EmptyData from "~/components/EmptyData";
import NewsletterWidget from "~/components/NewsletterWidget";
import SignUpWidget from "~/components/SignUpWidget";
import SocialSharing from "~/components/SocialSharing";
import { Resource, ResourceTypes } from "~/services/resource";
import Trans from "~/types/translationKeys";
import { getQueryParams } from "~/utils/common";
import { formatDate } from "~/utils/format";
import styles from "./ResourceBlogDetails.module.scss";

interface ResourceBlogDetailsProps {
  resource: Resource;
  type: ResourceTypes;
}

const ResourceBlogDetails: FC<ResourceBlogDetailsProps> = ({
  resource,
  type,
}) => {
  const url = `${process.env.NEXT_PUBLIC_LANDING_URL}/resources/${type}/${resource.slug}`;
  const isBlog = [ResourceTypes.BLOGS, ResourceTypes.OLD_BLOGS].includes(type);
  const [_, setCookies] = useCookies(["utm_blog"]);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (resource.id) {
      setCookies("utm_blog", resource.id);
    }
  }, [resource?.id]);

  const renderCategory = () => {
    switch (type) {
      case ResourceTypes.RELEASES: {
        return (
          <Link href="/resources/releases" className={styles.category}>
            {type.toLowerCase()}
          </Link>
        );
      }
      case ResourceTypes.CASE_STUDY: {
        return (
          <Link href="/resources/case-study" className={styles.category}>
            {t(Trans.case_studies)}
          </Link>
        );
      }
      case ResourceTypes.BLOGS: {
        // if (!resource.categories?.length) {
        //   return (
        //     <Link href="/resources/blogs" className={styles.category}>
        //       Blogs
        //     </Link>
        //   );
        // }

        return (
          <div className="flex flex-wrap gap-1">
            {resource.categories?.map((c) => (
              <div className={styles.category_item} key={c.id}>
                {c.name}
              </div>
            ))}
          </div>
        );
      }
    }
  };

  return (
    <section className={styles.resource_blog_details}>
      <Container className={styles.container}>
        {resource.toc && isBlog && (
          <div className={styles.left} data-aos="fade-down">
            <p className={styles.title}>{t(Trans.table_of_content)}</p>

            <div
              className={styles.description}
              dangerouslySetInnerHTML={{ __html: resource.toc }}
            />
          </div>
        )}

        <div className={styles.center}>
          <div className="flex items-center gap-x-1" data-aos="fade-down">
            {renderCategory()}
            {resource.category && (
              <Fragment>
                <p className={styles.category}>/</p>
                <p className={styles.category}>{t(Trans.category)}</p>
                <Link
                  href={`/resources/${type}?categories=${resource.category?.slug}`}
                  className={styles.category}
                >
                  {t(Trans.category)}
                </Link>
              </Fragment>
            )}
          </div>

          <h1 className={styles.title} data-aos="fade-up">
            {resource.name}
          </h1>

          <div
            className="flex items-center justify-between my-7 md:my-8"
            data-aos="fade-up"
          >
            <p className={styles.date}>
              {formatDate(
                new Date(resource.createdAt),
                "MMM dd yyyy",
                i18n.language
              )}
            </p>

            <SocialSharing url={url} dataAos="fade-up" />
          </div>

          {resource.toc && (
            <div className={styles.table_of_content} data-aos="fade-up">
              <p className={styles.title}>{t(Trans.table_of_content)}</p>

              <div
                className={styles.description}
                dangerouslySetInnerHTML={{ __html: resource.toc }}
              />
            </div>
          )}

          <div className={styles.thumbnail} data-aos="fade-up">
            <Image
              src={resource.thumbnail?.url || "/images/card_default.png"}
              alt={resource.name}
              fill
            />
          </div>

          <div
            className={styles.description}
            dangerouslySetInnerHTML={{ __html: resource.description }}
            data-aos="fade-up"
          />

          <SocialSharing
            url={url}
            className="xl:justify-end"
            dataAos="fade-up"
          />

          {isBlog && (
            <div className="flex flex-col gap-y-7 xl:hidden mt-7">
              <SignUpWidget blogId={resource.id} />
              <NewsletterWidget />
            </div>
          )}

          <Divider className="my-8" />

          {isBlog && (
            <>
              <h3 className={styles.article_title}>
                {t(Trans.related_articles)}
              </h3>

              {resource.related_blogs?.length ? (
                <div className={styles.article_list}>
                  {resource.related_blogs.slice(0, 2).map((b) => (
                    <Card
                      name={b.name}
                      href={`/resources/${type}/${b.slug}`}
                      thumbnail={b.thumbnail?.url}
                      key={b.id}
                      time={b.createdAt}
                      variant="secondary"
                    />
                  ))}
                </div>
              ) : (
                <EmptyData text={t(Trans.no_data)} size="small" />
              )}
            </>
          )}
        </div>

        {isBlog && (
          <div className={styles.right}>
            <SignUpWidget blogId={resource.id} />
            <NewsletterWidget />
          </div>
        )}
      </Container>
    </section>
  );
};

export default ResourceBlogDetails;
